/**
 * BYO v2 — code-container hydration for the edit modal (split out of
 * {@link useByoEditSource}).
 *
 * Once the source's attached container is in the store (loaded by
 * {@link useByoSourceHydration}), this hook:
 *
 *   - classifies the tab from the container's own `reusable` field, once per
 *     source load ("Use Existing Function" vs "Provide Your Own Function")
 *   - decodes the code + code_config.packages → decodeCodeContainerContent
 *   - reads code_config.extra_data     → runtime variables + performance config
 *
 * and owns the resulting code-draft state (code / mode / pick / packages /
 * runtime vars / performance config).
 */

import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useSelector } from 'react-redux';
import {
  BYO_PERF_DEFAULTS,
  DEFAULT_CODE,
  deserializePerformanceConfig,
  deserializeRuntimeVars,
  type BYOPerformanceConfig,
  type ByoCodeMode,
  type RuntimeVar,
} from 'new/components/flows/types/byo/core/model';
import {
  selectCodeContainersById,
  type CodeContainerEntity,
} from 'new/state/entities/selectors/transformSelectors';
import { decodeCodeContainerContent } from 'new/components/flows/types/byo/core/services';

export interface UseByoCodeContainerHydrationArgs {
  /** Source id — a change re-arms classification and decoding. */
  id?: number;
  /** The container attached to the source (from useByoSourceHydration). */
  codeContainerId: number | string | null;
  /** Source hydration still in flight — decoded content locks once it settles. */
  loading: boolean;
}

export interface UseByoCodeContainerHydration {
  /** The attached container entity, once it is in the store. */
  container: CodeContainerEntity | null | undefined;
  /** True once the attached container's content has been decoded into the draft. */
  contentDecoded: boolean;
  /**
   * Is the container CURRENTLY attached to the source a shared reusable one
   * (vs. one this source owns)? Captured at classification and re-armed by
   * save() after every successful save.
   */
  attachedIsReusableRef: MutableRefObject<boolean>;

  code: string;
  setCode: (code: string) => void;
  codeMode: ByoCodeMode;
  setCodeMode: (mode: ByoCodeMode) => void;
  predefinedId: number | string | null;
  setPredefinedId: (id: number | string | null) => void;
  packages: string[];
  setPackages: (next: string[]) => void;
  runtimeVars: RuntimeVar[];
  setRuntimeVars: (next: RuntimeVar[]) => void;
  performanceConfig: BYOPerformanceConfig;
  setPerformanceConfig: (next: BYOPerformanceConfig) => void;
}

export function useByoCodeContainerHydration({
  id,
  codeContainerId,
  loading,
}: UseByoCodeContainerHydrationArgs): UseByoCodeContainerHydration {
  const [code, setCode] = useState<string>(DEFAULT_CODE);
  const [codeMode, setCodeMode] = useState<ByoCodeMode>('custom');
  const [predefinedId, setPredefinedId] = useState<number | string | null>(null);
  const [packages, setPackages] = useState<string[]>([]);
  const [runtimeVars, setRuntimeVars] = useState<RuntimeVar[]>([]);
  const [performanceConfig, setPerformanceConfig] =
    useState<BYOPerformanceConfig>(BYO_PERF_DEFAULTS);

  const transforms = useSelector(selectCodeContainersById);
  // Classify the tab from `container.reusable` exactly once per source load —
  // not on every re-fetch of the container (e.g. after a save updates it in
  // the store), which would otherwise snap the tab back mid-edit.
  const modeClassifiedRef = useRef(false);
  // Is the container CURRENTLY attached to this source (i.e. what
  // `existingContainerId` would point `save()` at) one we own, or a shared
  // reusable one? Captured alongside tab classification, and re-armed after
  // every successful save to reflect whatever save() just attached — see
  // `save()`'s use of this (useByoSourceSave) for why it matters.
  const attachedIsReusableRef = useRef(false);
  // The attached container's content (code/packages/vars/perf) is decoded
  // into the draft while the source is hydrating, then LOCKED: any later
  // re-fetch of the same container (a "Use Existing" pick of that same id,
  // or the store update after a save) produces a new store object, and
  // re-decoding it would silently overwrite whatever the user has since
  // typed into "Provide Your Own Function" (NEX-22636).
  // State (not a ref) so anything waiting on hydration — the dirty-tracking
  // baseline in useByoEditSource — sees the decoded values in the SAME render.
  const [contentDecoded, setContentDecoded] = useState(false);
  const contentLockedRef = useRef(false);

  // Re-arm per source load. Declared before the decode effect so, on an id
  // change, the reset runs first in the same commit (as it did when this
  // lived at the top of the source-load effect).
  useEffect(() => {
    modeClassifiedRef.current = false;
    setContentDecoded(false);
    contentLockedRef.current = false;
  }, [id]);

  // Decode the code container + packages once it is in the store. Read from the
  // normalized entity (state.entities.transforms[id]) — same surface useStartSource uses.
  const container = codeContainerId != null ? transforms[codeContainerId] : null;
  useEffect(() => {
    if (!container) return;
    // Tab classification, once per source load: the attached container's own
    // `reusable` field is authoritative. `reusable: true` → "Use Existing
    // Function", pre-selected on this exact container (its id doubles as
    // `predefinedId` — no separate marker needed, it IS the container that
    // came back on this source). `reusable: false`/falsy → "Provide Your Own
    // Function", editing this container's code directly; save() updates it
    // in place via `existingContainerId`. Confirmed bug, fixed 2026-08-20.
    if (!modeClassifiedRef.current) {
      modeClassifiedRef.current = true;
      attachedIsReusableRef.current = !!container.reusable;
      if (container.reusable) {
        setCodeMode('predefined');
        setPredefinedId(codeContainerId);
      } else {
        setCodeMode('custom');
      }
    }
    if (contentLockedRef.current) return;
    setContentDecoded(true);
    const decoded = decodeCodeContainerContent(container);
    setCode(decoded.code || DEFAULT_CODE);
    setPackages(decoded.packages);
    // extra_data is persisted under code_config (see buildCodeContainerPayload),
    // NOT at the container root — read it from there so edits hydrate the
    // saved values instead of resetting.
    const extraData = container.code_config?.extra_data;
    setRuntimeVars(deserializeRuntimeVars(extraData));
    // No runtime_config (or unparseable extra_data) keeps the defaults.
    const savedPerf = deserializePerformanceConfig(extraData);
    if (savedPerf) setPerformanceConfig(savedPerf);
  }, [container, codeContainerId, id]);

  useEffect(() => {
    if (!loading && contentDecoded) contentLockedRef.current = true;
  }, [loading, contentDecoded]);

  return {
    container,
    contentDecoded,
    attachedIsReusableRef,
    code,
    setCode,
    codeMode,
    setCodeMode,
    predefinedId,
    setPredefinedId,
    packages,
    setPackages,
    runtimeVars,
    setRuntimeVars,
    performanceConfig,
    setPerformanceConfig,
  };
}

export default useByoCodeContainerHydration;
